"use client";

import * as React from "react";
import Link from "next/link";
import { ArrowRight, BookOpen, type LucideIcon } from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { type AccentColor } from "../lib/shared";
import { ACCENT_CYCLE } from "../lib/shared";
import { NamedIcon } from "../icon-component";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

type Accent = keyof typeof ACCENT;

interface ArticleSummary {
  categorySlug: string;
  slug: string;
  title: string;
  excerpt: string;
  iconName: string;
  accent: AccentColor;
  readingTime: number;
}

interface CategorySummary {
  slug: string;
  label: string;
  overline: string;
  longDescription: string;
  description: string;
  iconName: string;
  accent: AccentColor;
}

function ArticleCard({ article, index }: { article: ArticleSummary; index: number }) {
  const accent = ACCENT_CYCLE[index % ACCENT_CYCLE.length];
  const a = ACCENT[accent];
  return (
    <Link
      href={`/insights/${article.categorySlug}/${article.slug}/`}
      className="group block h-full"
    >
      <div className={`relative h-full glass-card rounded-2xl p-5 overflow-hidden bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
        <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
        <div className="relative flex flex-col h-full">
          <div className="flex items-start justify-between mb-3">
            <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
              <NamedIcon name={article.iconName} className="size-5 text-white" />
            </div>
            <span className={`text-overline ${a.text} opacity-70`}>
              {String(index + 1).padStart(2, "0")} · {article.readingTime} min
            </span>
          </div>
          <h3 className={`text-heading-md mb-2 ${a.text}`}>
            {article.title}
          </h3>
          <p className="text-caption text-muted-foreground leading-relaxed mb-3 line-clamp-3 flex-1">
            {article.excerpt}
          </p>
          <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
            Read the essay <ArrowRight className="size-3" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export interface CategoryViewProps {
  category: CategorySummary;
  articles: ArticleSummary[];
  totalSections: number;
  longestRead: number;
  otherDomains: CategorySummary[];
}

export function CategoryView({ category, articles, totalSections, longestRead, otherDomains }: CategoryViewProps) {
  const a = ACCENT[category.accent];
  return (
    <>
      <PageHero
        icon={(() => <NamedIcon name={category.iconName} className="size-3.5" />) as unknown as LucideIcon}
        overline={category.overline}
        overlineClassName={a.text}
        title={
          <>
            {category.label} — <span className="text-gradient-premium">The Complete Library.</span>
          </>
        }
        subtitle={category.longDescription}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Insights", href: "/insights/" },
          { label: category.label },
        ]}
        badge={`${articles.length} articles · ${category.overline}`}
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${a.pill} mb-5`}>
              <NamedIcon name={category.iconName} className={`size-3.5 ${a.text}`} />
              <span className={`text-overline ${a.text}`}>Domain Overview</span>
            </div>
            <h2 className="text-display-md mb-5">
              {category.label} as an operating discipline.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              {category.description} The articles below are not a collection. They are a sequence — each one building on the previous, each one applying the operating method to a specific operational question within the {category.label.toLowerCase()} domain.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Begin at the top. Read in order. The full context accumulates with each essay, and the answers become sharper as the operating framework clarifies.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { metric: String(articles.length), label: "Articles", sub: "In this domain", accent: category.accent },
              { metric: String(totalSections), label: "Sections", sub: "Of operating material", accent: ACCENT_CYCLE[1] },
              { metric: String(longestRead), suffix: " min", label: "Longest Read", sub: "The deepest essay", accent: ACCENT_CYCLE[2] },
              { metric: "1", label: "Method", sub: "Applied across the domain", accent: ACCENT_CYCLE[3] },
            ].map((s) => {
              const ac = ACCENT[s.accent as Accent];
              return (
                <div
                  key={s.label}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border}`}
                >
                  <div className={`text-display-sm font-bold ${ac.text} leading-none mb-2`}>
                    {s.metric}{s.suffix || ""}
                  </div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                  <div className="text-caption text-muted-foreground">{s.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${a.pill} mb-3`}>
              <span className={`text-overline ${a.text}`}>All Articles</span>
            </div>
            <h2 className="text-display-md">Every essay in {category.label}.</h2>
          </div>
          <Link
            href="/insights/"
            className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} hover:gap-2.5 transition-all`}
          >
            ← Back to all domains
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((article, i) => (
            <ArticleCard key={article.slug} article={article} index={i} />
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <span className="text-overline text-purple">Adjacent Domains</span>
          </div>
          <h2 className="text-display-md mb-4">Continue across the library.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The {category.label.toLowerCase()} domain does not exist in isolation. The other eight domains complete the operating picture. Begin with whichever addresses the immediate question.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {otherDomains.map((other) => {
            const ac = ACCENT[other.accent as Accent];
            return (
              <Link
                key={other.slug}
                href={`/insights/${other.slug}/`}
                className="group block h-full"
              >
                <div className={`relative h-full glass-card rounded-2xl p-5 overflow-hidden bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}>
                  <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                  <div className="relative">
                    <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center mb-3`}>
                      <NamedIcon name={other.iconName} className="size-5 text-white" />
                    </div>
                    <span className={`text-overline ${ac.text} mb-1 block`}>{other.overline}</span>
                    <h3 className={`text-heading-md mb-2 ${ac.text}`}>{other.label}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3 line-clamp-2">
                      {other.description}
                    </p>
                    <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${ac.text} group-hover:gap-2.5 transition-all`}>
                      Enter domain <ArrowRight className="size-3" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      <RelatedPages
        title="Adjacent Reading"
        items={[
          { title: "The Insights Library", description: "All nine domains and the complete index.", href: "/insights/", icon: BookOpen, accent: category.accent as "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" },
          { title: "About Dr. Jyoti Kush", description: "The full arc — from medicine to sovereign-scale operations.", href: "/about/", icon: BookOpen, accent: "purple" },
          { title: "The Expertise Domains", description: "The five interconnected domains of practice.", href: "/expertise/", icon: BookOpen, accent: "emerald" },
          { title: "The 70% Project", description: "The flagship initiative documenting the operating case for the talent the world overlooks.", href: "/the-70-percent-project/", icon: BookOpen, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
